import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, of, take, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ResultResponse } from '../../../shared/models/common/result-response';
import { ForgotPasswordRequest } from '../../../shared/models/user/forgot-password-request';
import { PasswordChangeAuthenticated } from '../../../shared/models/user/password-change-authenticated';
import { PasswordChangeUnauthenticated } from '../../../shared/models/user/password-change-unauthenticated';
import { QRCodeResponse } from '../../../shared/models/user/qr-code-response';
import { UpdateUserRequest } from '../../../shared/models/user/update-user-request';
import { UserModel } from '../../../shared/models/user/user-model';
import { NotificationsService } from '../../../shared/services/notifications.service';
import * as Sentry from '@sentry/angular';
import { UserTour } from '../../../shared/services/customization.service';
import { CookieService } from 'ngx-cookie-service';
import { ErrorEvent } from '@progress/kendo-angular-upload';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private apiUrl: string = environment.apiUrl;
  private userSubject = new BehaviorSubject<UserModel>(
    '' as unknown as UserModel,
  );
  user$ = this.userSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private notificationService: NotificationsService,
    private cookieService: CookieService,
  ) {}

  finishTour(tour: UserTour): Observable<any> {
    const url = this.apiUrl + 'api/v2/User/FinishTour';
    return this.httpClient.post<any>(url, {}, { params: { tour } });
  }

  loadUserData(): Observable<UserModel | null> {
    const url = this.apiUrl + 'api/v2/user/info';

    return this.httpClient.get<UserModel>(url).pipe(
      tap((user) => {
        if (user) {
          Sentry.setUser({
            email: user.Email,
            id: String(user.UserId),
            // activeProjectID: 5,
          });
          this.userSubject.next(user);
        } else {
          // Handle the case where the API response is null or undefined
          this.notificationService.showError('User data is null or undefined');
          // this.notificationService.showError();
        }
      }),
      catchError((error) => {
        // Handle errors here, e.g., show an error notification
        // this.notificationService.showError('Failed to load user data');
        this.notificationService.showError(error);
        return of(null); // Return an observable with a null value to avoid breaking the stream
      }),
    );
  }

  getUser(): Observable<UserModel> {
    return this.user$;
  }

  acceptLicenseAgreement(): Observable<boolean> {
    const url = this.apiUrl + 'api/v2/User/agreetoeula';

    return this.httpClient.patch<boolean>(url, {});
  }

  deleteAvatar(): Observable<{ result: string; error: unknown }> {
    const url = this.apiUrl + 'api/v2/Cloudinary/RemoveProfilePicture';

    return this.httpClient.delete<{ result: string; error: unknown }>(url).pipe(
      tap((response) => {
        if (response.result === 'ok') {
          this.loadUserData().pipe(take(1)).subscribe();
        } else {
          this.notificationService.showError('Error removing profile picture');
        }
      }),
    );
  }

  onUploadSuccess() {
    this.loadUserData().pipe(take(1)).subscribe();
  }

  onUploadError(event: ErrorEvent) {
    this.notificationService.showError(
      ((event.response as unknown as HttpErrorResponse).error
        .Errors as string) ?? 'ERROR.profilePictureUpload.FileTooLarge',
    );
  }

  updatePersonalInformation(
    updateUserRequest: UpdateUserRequest,
  ): Observable<ResultResponse> {
    const url = this.apiUrl + 'api/v2/User/updateUser';

    return this.httpClient.post<ResultResponse>(url, updateUserRequest).pipe(
      tap(() => {
        this.loadUserData().pipe(take(1)).subscribe();
        this.notificationService.showSuccessNotification(
          'NotificationMessages.UpdateSuccesful',
        );
      }),
    );
  }

  forgotPassword(
    forgotPasswordRequest: ForgotPasswordRequest,
  ): Observable<any> {
    const url = this.apiUrl + 'api/v2/User/forgotpassword';
    return this.httpClient.post(url, forgotPasswordRequest);
  }

  isValidResetPasswordToken(token: string): Observable<boolean> {
    const url =
      this.apiUrl + `api/v2/User/isvalidresetguid/${encodeURIComponent(token)}`;
    return this.httpClient.get<boolean>(url);
  }

  changePasswordWhileUserUnauthenticated(
    request: PasswordChangeUnauthenticated,
  ): Observable<ResultResponse> {
    const url = this.apiUrl + 'api/v2/User/changepasswordguid';
    return this.httpClient.post<ResultResponse>(url, request);
  }

  changePasswordWhileUserAuthenticated(
    request: PasswordChangeAuthenticated,
  ): Observable<ResultResponse> {
    const url = this.apiUrl + 'api/v2/User/changepassword';
    return this.httpClient.post<ResultResponse>(url, request);
  }

  public getQrCode(): Observable<QRCodeResponse> {
    const url = this.apiUrl + `api/v2/User/getqrcode`;
    return this.httpClient.get<QRCodeResponse>(url);
  }

  redirectToAcademy() {
    if (this.userSubject.value?.AccessToAcademy) {
      if (this.cookieService.check('access_token')) {
        window.open(
          'https://academy.dynamicmanaging.com?mo_jwt_token=' +
            this.cookieService.get('access_token'),
          '_blank',
          'noopener noreferrer',
        );
      }
    }
  }
}
